import { useMemberSubscription } from "@gymflow/api";
import { DurationType } from "@gymflow/common";
import { DATE_ONLY_FORMAT, iso8601WithoutTz } from "@gymflow/helpers";
import {
  Button,
  CloseIcon,
  FormLabeledSwitch,
  InfoBlock,
  WarningBlock,
} from "apps/portal/src/components/atoms";
import { FormSingleDatePicker } from "apps/portal/src/components/atoms/form/FormSingleDatePicker";
import { CancelIcon } from "apps/portal/src/components/atoms/icons/CancelIcon";
import { DatePickerDayRedDot } from "apps/portal/src/components/molecules/DatePicker/components/DatePickerDayRedDot";
import { generateDates, getDateTimeInGym } from "apps/portal/src/helpers/date";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { parse } from "date-fns";
import { FormikProvider, useFormik } from "formik";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { ToastContext } from "../../../../../providers/ToastProvider/context";
import { FormCancellationReasonSelect } from "./components/FormCancellationReasonSelect";
import {
  cancelMembershipFormSchema,
  INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
} from "./constants";
import { CancelMembershipFormProps, CancelMembershipFormValues } from "./types";

export const CancelMembershipForm = ({
  onClose,
  membership,
  memberId,
  linkedMembers,
}: CancelMembershipFormProps) => {
  const { t } = useTranslation();
  const { timezone } = useClubSettings();
  const { api } = useGymflowModels();
  const { notifyDanger } = useContext(ToastContext);

  const { cancelSubscriptionMutation } = useMemberSubscription(
    { api, tz: timezone },
    {
      onError: (error) => {
        if (isAxiosError(error)) {
          notifyDanger(error);
        }
      },
    },
  );

  const formik = useFormik<CancelMembershipFormValues>({
    initialValues: INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
    validationSchema: toFormikValidationSchema(cancelMembershipFormSchema),
    onSubmit: async ({ isChangeImmediately, cancellationReason, date }) => {
      if (!cancellationReason) {
        return;
      }

      await cancelSubscriptionMutation.mutateAsync({
        subscriptionId: membership.id,
        userMemberId: memberId,
        cancellationReasonId: cancellationReason.value,
        effectiveImmediately: isChangeImmediately ? true : undefined,
        cancellationDate:
          !isChangeImmediately && date
            ? (DateTime.fromJSDate(date)
                .endOf("day")
                .setZone(timezone)
                .endOf("day")
                .toUTC()
                .set({ millisecond: 0 })
                .toISO({ suppressMilliseconds: true }) ?? "")
            : undefined,
      });
      onClose();
    },
  });

  const { isChangeImmediately } = formik.values;

  // Copy-past from previous variant, refactor it
  const cancellingDates = useMemo(
    () =>
      generateDates({
        intervalType: (DurationType as any).format(
          membership.billingType,
          true,
        ),
        intervalValue: membership.billingPeriod ?? 0,
        start: parse(membership.endDate, iso8601WithoutTz, new Date()),
        dateFormat: DATE_ONLY_FORMAT,
      }).map((e) => DateTime.fromISO(e).plus({ days: 1 })),
    [membership.billingPeriod, membership.billingType, membership.endDate],
  );

  const disabledDate = useMemo(() => {
    const nowInGym = getDateTimeInGym(timezone);

    return {
      before: nowInGym.add(1, "day").toDate(),
    };
  }, [timezone]);

  const onCloseSidebar = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  return (
    <FormikProvider value={formik}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between gap-4 p-6 pb-0">
            <div className="flex flex-row items-start justify-between">
              <div className="border-error-50 bg-error-100 flex items-center rounded-full border-8 p-1.5">
                <CancelIcon className="size-6" />
              </div>
              <div className="cursor-pointer" onClick={() => onCloseSidebar()}>
                <CloseIcon className="size-6" />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-gray-950">
                {t("components.cancelMembershipSidebar.title")}
              </div>
              <div className="text-sm font-medium text-gray-500">
                {t("components.cancelMembershipSidebar.description")}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 px-6 pt-4">
            {membership.id !== membership.primarySubscriptionId && (
              <WarningBlock
                text={t(
                  "components.cancelMembershipSidebar.warningBlock.partOfShared",
                )}
              />
            )}
            {membership.id === membership.primarySubscriptionId &&
              linkedMembers &&
              linkedMembers?.length > 1 && (
                <WarningBlock
                  text={t(
                    "components.cancelMembershipSidebar.warningBlock.removedForAllLinkedMembers",
                  )}
                />
              )}
            <FormCancellationReasonSelect />
            <FormLabeledSwitch<CancelMembershipFormValues>
              name="isChangeImmediately"
              label={t("components.cancelMembershipSidebar.switch.label")}
              labelClassName="font-medium text-base text-gray-950"
            />
            {!isChangeImmediately && (
              <>
                <FormSingleDatePicker<CancelMembershipFormValues>
                  name="date"
                  disabled={disabledDate}
                  autoApply
                  components={{
                    Day: ({ day: { date }, day, ...dayProps }) => (
                      <DatePickerDayRedDot
                        day={day}
                        {...dayProps}
                        showRedDot={cancellingDates.some((d) =>
                          d.hasSame(
                            DateTime.fromISO(date.toISOString()),
                            "day",
                          ),
                        )}
                      />
                    ),
                  }}
                  label={t("components.cancelMembershipSidebar.data.label")}
                />
                <InfoBlock
                  text={t("components.cancelMembershipSidebar.infoBlock")}
                  className="items-center"
                />
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-t-gray-300 p-6">
          <Button
            intent="default"
            className="w-full"
            onClick={() => onCloseSidebar()}
          >
            {t("common.cancel")}
          </Button>
          <Button
            intent="danger"
            className="w-full"
            onClick={async () => await formik.submitForm()}
          >
            {t("common.confirm")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
